import { getModelCatalogEntry } from "./modelCatalog";

/**
 * Agent base cost — the standing, configuration-derived token footprint an
 * agent carries into EVERY turn: system prompt + tool definitions + tool
 * guidance + knowledge + scoped resources, priced by the agent's model.
 *
 * This is deliberately NOT a runtime estimate. It excludes the user request,
 * model output, loop iterations, and accumulated conversation history. The
 * value changes only when the agent's configuration changes — add a tool,
 * lengthen the prompt, attach knowledge, or switch the model, and it moves.
 *
 * Two outputs are produced:
 *   - rawTokens: model-independent size of the configured payload (the "raw").
 *   - tier (1-4, $ to $$$$): folds in the model's relative price so a heavy
 *     prompt on a cheap model can rank below a light prompt on a costly one.
 *
 * Enabled tools are stored as names only, so a tool's injected schema size is
 * approximated by a flat per-tool weight. Every per-resource weight and tier
 * threshold lives in BASE_COST_CONFIG so the scale can be recalibrated in one
 * place without touching callers.
 */
export const BASE_COST_CONFIG = {
  charsPerToken: 4,
  // Fixed scaffolding the runtime injects every turn (base framing, formatting
  // rules) regardless of how the agent is configured.
  baselineTokens: 250,
  perToolTokens: 110,
  perMcpToolTokens: 110,
  perKnowledgeFileTokens: 40,
  perKnowledgeTableTokens: 40,
  perConfiguredTableTokens: 40,
  perWorkflowTokens: 60,
  perAgentTokens: 40,
  // weightedScore = rawTokens * (1 + (modelCostTier - 1) * modelTierWeightStep)
  modelTierWeightStep: 0.5,
  defaultModelCostTier: 1,
  // Exclusive upper bounds on weightedScore for tiers 1, 2, 3; >= last => 4.
  tierThresholds: [2000, 4000, 8000],
};

export const BASE_COST_TIER_MAX = 4;

export const BASE_COST_TIER_LABELS = {
  1: "Low",
  2: "Moderate",
  3: "High",
  4: "Very high",
};

export function estimateTextTokens(text) {
  if (typeof text !== "string") return 0;
  const length = text.trim().length;
  if (length === 0) return 0;
  return Math.ceil(length / BASE_COST_CONFIG.charsPerToken);
}

const countList = (value) => (Array.isArray(value) ? value.length : 0);

const sumInstructionTokens = (toolInstructions) => {
  if (!toolInstructions || typeof toolInstructions !== "object") return 0;
  return Object.values(toolInstructions).reduce(
    (total, instruction) => total + estimateTextTokens(instruction),
    0
  );
};

const countMcpRemoteTools = (mcpServerRefs) => {
  if (!Array.isArray(mcpServerRefs)) return 0;
  return mcpServerRefs.reduce(
    (total, ref) => total + countList(ref?.toolAllowlist),
    0
  );
};

function resolveModelCostTier(model) {
  const entry = model ? getModelCatalogEntry(model) : null;
  const tier = entry?.costTier;
  return Number.isFinite(tier) && tier >= 1
    ? tier
    : BASE_COST_CONFIG.defaultModelCostTier;
}

function tierFromScore(score) {
  const thresholds = BASE_COST_CONFIG.tierThresholds;
  for (let index = 0; index < thresholds.length; index += 1) {
    if (score < thresholds[index]) return index + 1;
  }
  return BASE_COST_TIER_MAX;
}

/**
 * Compute the base cost of an agent from its configuration.
 *
 * @param {object} config flattened agent configuration. Recognized fields:
 *   systemPrompt, model, enabledTools, toolInstructions,
 *   knowledgeManualContext, knowledgeFiles, knowledgeTables, configuredTables,
 *   runWorkflowConfigs, configuredAgents, mcpServerRefs. Unknown/missing fields
 *   contribute nothing.
 * @returns {{ rawTokens: number, tier: number, modelCostTier: number,
 *   weightedScore: number, breakdown: Record<string, number> }}
 */
export function computeAgentBaseCost(config = {}) {
  const {
    systemPrompt,
    model,
    enabledTools,
    toolInstructions,
    knowledgeManualContext,
    knowledgeFiles,
    knowledgeTables,
    configuredTables,
    runWorkflowConfigs,
    configuredAgents,
    mcpServerRefs,
  } = config;

  const breakdown = {
    baseline: BASE_COST_CONFIG.baselineTokens,
    systemPrompt: estimateTextTokens(systemPrompt),
    tools: countList(enabledTools) * BASE_COST_CONFIG.perToolTokens,
    toolInstructions: sumInstructionTokens(toolInstructions),
    knowledgeContext: estimateTextTokens(knowledgeManualContext),
    knowledgeFiles:
      countList(knowledgeFiles) * BASE_COST_CONFIG.perKnowledgeFileTokens,
    knowledgeTables:
      countList(knowledgeTables) * BASE_COST_CONFIG.perKnowledgeTableTokens,
    configuredTables:
      countList(configuredTables) * BASE_COST_CONFIG.perConfiguredTableTokens,
    workflows:
      countList(runWorkflowConfigs) * BASE_COST_CONFIG.perWorkflowTokens,
    configuredAgents:
      countList(configuredAgents) * BASE_COST_CONFIG.perAgentTokens,
    mcpTools:
      countMcpRemoteTools(mcpServerRefs) * BASE_COST_CONFIG.perMcpToolTokens,
  };

  const rawTokens = Object.values(breakdown).reduce(
    (total, value) => total + value,
    0
  );
  const modelCostTier = resolveModelCostTier(model);
  const modelWeight =
    1 + (modelCostTier - 1) * BASE_COST_CONFIG.modelTierWeightStep;
  const weightedScore = rawTokens * modelWeight;
  const tier = tierFromScore(weightedScore);

  return { rawTokens, tier, modelCostTier, weightedScore, breakdown };
}

/**
 * Convenience adapter for a saved agent record (the AgentDTO / list shape),
 * where knowledge is nested under `agent.knowledge`. Maps it onto the flat
 * input expected by computeAgentBaseCost. Missing fields contribute nothing,
 * so trimmed payloads degrade gracefully.
 *
 * @param {object} agent agent definition/DTO
 * @returns ReturnType<typeof computeAgentBaseCost>
 */
export function computeAgentBaseCostFromDefinition(agent = {}) {
  const knowledge = agent.knowledge || {};
  return computeAgentBaseCost({
    systemPrompt: agent.systemPrompt,
    model: agent.model,
    enabledTools: agent.enabledTools,
    toolInstructions: agent.toolInstructions,
    knowledgeManualContext: knowledge.manualContext,
    knowledgeFiles: knowledge.files,
    knowledgeTables: knowledge.tables,
    configuredTables: agent.configuredTables,
    runWorkflowConfigs: agent.runWorkflowConfigs,
    configuredAgents: agent.configuredAgents,
    mcpServerRefs: agent.mcpServerRefs,
  });
}

export function formatTokenCount(tokens) {
  const value = Number.isFinite(tokens) ? Math.max(0, Math.round(tokens)) : 0;
  if (value < 1000) return String(value);
  const thousands = value / 1000;
  if (value < 10000) {
    return `${thousands.toFixed(1).replace(/\.0$/, "")}k`;
  }
  return `${Math.round(thousands)}k`;
}
